import React, { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import FocusTrap from 'focus-trap-react';
import { Headline, IconButton } from '@oneaudi/unified-web-alpha-components';
import CancelIcon from '../assets/icons/CancelIcon';
import {
  DigitalLeadsWrapper,
  DigitalLeadsModal,
  CloseModal,
  Content,
  Header,
  InputSection,
  VehicleDetailSection,
  Title,
  Inputs,
  InputField,
  BlackBackground,
  Sticky,
  SectionWrapper,
} from './DitigalLeads.styles';
import VehicleInfo from './VehicleInfo/VehicleInfo';
import Submit from './Submit';
import useDealerInfo from '../hooks/useDealerInfo';
import useVehicleInfo from '../hooks/useVehicleInfo';
import useLeadsBuilder from '../hooks/useLeadsBuilder';
import useLockBodyScroll from '../hooks/useLockBodyScroll';
import LegalDisclaimer from './LegalDisclaimer';
import ResultModal from './ResultModal';
import { selectLocalConfig, setModalIsClose } from '../redux/localConfigReducer';
import { selectForm } from '../redux/formReducer';
import { removeVehicleInfo, selectDealerAndVehicleData } from '../redux/dealerAndVehicleReducer';
import useSourceOfLead from '../hooks/useSourceOfLead';
import {
  useGenericFormLoad,
  useSuccessfulFormSubmission,
  useUnsuccessfulFormError,
  useIxisFormTracking,
} from '../hooks/useAnalyticsEventsIxis';
import { convertToPromotionsType } from '../functions/convertToPromotionsType';
import { selectAppConfig, setClearVehicleData, setRemoveOffers } from '../redux/appConfigReducer';
import { StockCarTypes } from '../types';
import getDataAttributes from '../functions/getDataAttributes';
import { IS_T1_PAGE } from '../constants/environment';

// ============================================================================
// COMPONENT CONFIGURATION
// ============================================================================

const isLegacy = process.env.IS_LEGACY;

// ============================================================================
// HELPER: Form Name Resolver
// ============================================================================

function getFormName(isGenericForm: boolean, leadSource?: string): string {
  if (isGenericForm) return 'Generic Lead';
  if (leadSource === 'special_offers' || leadSource === 'offers') return 'Promotions Lead';
  return 'VTP Lead';
}

// ============================================================================
// SUBCOMPONENT: Modal Header
// ============================================================================

interface ModalHeaderProps {
  dealerName?: string;
  onClose: () => void;
}

const ModalHeader: React.FC<ModalHeaderProps> = ({ dealerName, onClose }) => {
  const { t } = useTranslation();

  const headerText = dealerName
    ? t('digitalLeads.contactDealer', { dealerName })
    : t('digitalLeads.contactDealerInYourArea');

  return (
    <>
      <Sticky>
        {isLegacy ? (
          <CloseModal onClick={onClose} aria-label="close">
            <CancelIcon />
          </CloseModal>
        ) : (
          <IconButton
            aria-label="close"
            icon="cancel"
            onClick={onClose}
            size="xs"
            variant="tertiary"
            margin={[0, 0, 0, 0]}
          />
        )}
      </Sticky>

      {isLegacy ? (
        <Header>{headerText}</Header>
      ) : (
        <Headline variant={{ type: 'headline', order: '2', weight: 'normal' }} tag="h1">
          {headerText}
        </Headline>
      )}
    </>
  );
};

// ============================================================================
// SUBCOMPONENT: Form Section
// ============================================================================

interface FormSectionProps {
  title: string;
  children: React.ReactNode;
}

const FormSection: React.FC<FormSectionProps> = ({ title, children }) => {
  return (
    <SectionWrapper>
      {isLegacy ? (
        <Title>{title}</Title>
      ) : (
        <Headline
          variant={{ type: 'headline', order: '4', weight: 'normal' }}
          tag="h2"
          margin={['0', '0', '24px', '0']}
        >
          {title}
        </Headline>
      )}
      {children}
    </SectionWrapper>
  );
};

// ============================================================================
// SUBCOMPONENT: Form Inputs Renderer
// ============================================================================

interface InputItem {
  inputKey: string;
  Component: React.ReactNode;
}

interface FormInputsProps {
  topicOfInterest?: InputItem[] | null;
  tradeInVehicle?: InputItem[] | null;
  modelSelections?: InputItem[] | null;
  contactInfo: InputItem[];
  tradeInLabel: string;
  modelSelectionLabel: string;
  contactInfoLabel: string;
}

const FormInputs: React.FC<FormInputsProps> = ({
  topicOfInterest,
  tradeInVehicle,
  modelSelections,
  contactInfo,
  tradeInLabel,
  modelSelectionLabel,
  contactInfoLabel,
}) => {
  return (
    <Inputs>
      {topicOfInterest?.map((topic) => (
        <InputField key={topic.inputKey}>{topic.Component}</InputField>
      ))}

      {tradeInVehicle && (
        <FormSection title={tradeInLabel}>
          {tradeInVehicle.map((input) => (
            <InputField key={input.inputKey}>{input.Component}</InputField>
          ))}
        </FormSection>
      )}

      {modelSelections && (
        <FormSection title={modelSelectionLabel}>
          {modelSelections.map((model) => (
            <InputField key={model.inputKey}>{model.Component}</InputField>
          ))}
        </FormSection>
      )}

      <FormSection title={contactInfoLabel}>
        {contactInfo.map((input) => (
          <InputField key={input.inputKey}>{input.Component}</InputField>
        ))}
      </FormSection>
    </Inputs>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const DigitalLeads: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const digitalLeadsRef = useRef<HTMLDivElement>(null);

  // ========== SELECTORS ==========
  const { isModalOpen, leadId, error, errorMessage } = useSelector(selectLocalConfig);
  const contactForm = useSelector(selectForm);
  const { offers, dealerId, presetForm } = useSelector(selectAppConfig);
  const { vehicle, vehicleByCarline, vehicleByAudiCode } = useSelector(selectDealerAndVehicleData);

  // ========== CUSTOM HOOKS ==========
  const { dealer, isLoadingDealer } = useDealerInfo();
  const { isVehicleLoading } = useVehicleInfo();
  const { topicOfInterestInput, tradeInVehicleInputs, modelSelections, buildedForm } =
    useLeadsBuilder();
  const { specificSource: leadSource } = useSourceOfLead();

  // ========== VEHICLE DATA ==========
  const vehicleInView = vehicle || vehicleByCarline || vehicleByAudiCode;

  // ========== PROMOTION DATA ==========
  const promotion = React.useMemo(
    () => (offers ? convertToPromotionsType(offers) : undefined),
    [offers],
  );

  // ========== ANALYTICS TRACKING ==========
  const { vehicleWithEditedNames, vehicleDataForPromotions } = useIxisFormTracking({
    isModalOpen,
    vehicle,
    vehicleInView,
    offers,
    dealerId,
    leadSource,
    contactForm,
    promotion,
  });

  // Hook for generic form load
  useGenericFormLoad({ isModalOpen, presetForm, vehicle: undefined });

  // Hook for successful submission
  useSuccessfulFormSubmission({
    contactForm,
    leadId,
    vehicle: vehicleWithEditedNames ?? vehicleDataForPromotions,
    promotion,
  });

  // Hook for submission error
  useUnsuccessfulFormError({
    error,
    errorMessage,
    contactForm,
    vehicle: vehicleWithEditedNames ?? vehicleDataForPromotions,
    promotion,
  });

  // ========== HANDLERS ==========
  const handleCloseModal = () => {
    dispatch(setRemoveOffers());
    dispatch(removeVehicleInfo());
    dispatch(setClearVehicleData());
    dispatch(setModalIsClose());
    window.history.pushState('', document.title, window.location.pathname + window.location.search);
  };

  // Lock body scroll when modal is open
  useLockBodyScroll(isModalOpen);

  // ========== COMPUTED VALUES ==========
  const isGeneralForm = !!topicOfInterestInput;
  const formName = getFormName(isGeneralForm, leadSource);

  // ========== DATA ATTRIBUTES ==========
  const isTier3 = !IS_T1_PAGE;
  const modalDataAttributes = getDataAttributes(vehicleInView as StockCarTypes, formName, isTier3);

  // ========== RENDER ==========
  if (!isModalOpen) return null;

  return (
    <DigitalLeadsWrapper>
      <FocusTrap
        active={isModalOpen}
        focusTrapOptions={{ escapeDeactivates: false, clickOutsideDeactivates: true }}
      >
        {!leadId && !error ? (
          <DigitalLeadsModal ref={digitalLeadsRef} {...modalDataAttributes}>
            <ModalHeader dealerName={dealer?.name} onClose={handleCloseModal} />
            <Content>
              <InputSection>
                <FormInputs
                  topicOfInterest={topicOfInterestInput}
                  tradeInVehicle={tradeInVehicleInputs}
                  modelSelections={modelSelections}
                  contactInfo={buildedForm}
                  tradeInLabel={t('digitalLeads.inputs.vehicleDetails')}
                  modelSelectionLabel={t('digitalLeads.inputs.modelSelection')}
                  contactInfoLabel={t('digitalLeads.contactInfoLabel')}
                />
                <LegalDisclaimer />
                <Submit />
              </InputSection>

              <VehicleDetailSection>
                <VehicleInfo
                  dealer={dealer}
                  vehicle={vehicleInView}
                  isGeneralForm={isGeneralForm}
                  isLoadingDealer={isLoadingDealer}
                  isVehicleLoading={isVehicleLoading}
                />
              </VehicleDetailSection>
            </Content>
          </DigitalLeadsModal>
        ) : (
          <ResultModal handleCloseModal={handleCloseModal} isSuccess={!!leadId} />
        )}
      </FocusTrap>
      <BlackBackground onClick={handleCloseModal} />
    </DigitalLeadsWrapper>
  );
};

export default DigitalLeads;
